import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStrategy, duplicateStrategy } from '../api';
import { track, Events } from '../analytics';
import ShareBacktestModal from '../components/ShareBacktestModal';
import ExportStrategyModal from '../components/ExportStrategyModal';
import DownloadDataModal from '../components/DownloadDataModal';
import StatsGrid from './backtestResults/StatsGrid';
import PaperTradingCTA from './backtestResults/PaperTradingCTA';
import EmptyTradesCTA from './backtestResults/EmptyTradesCTA';
import PriceChartSection from './backtestResults/PriceChartSection';
import EquityCurveSection from './backtestResults/EquityCurveSection';
import IndicatorsSection from './backtestResults/IndicatorsSection';
import CorrelationSection from './backtestResults/CorrelationSection';
import AdvancedAnalyticsSection from './backtestResults/AdvancedAnalyticsSection';
import TradeLogSection from './backtestResults/TradeLogSection';
import QuickEditPanel from './backtestResults/QuickEditPanel';
import WalkForwardPanel from './backtestResults/WalkForwardPanel';
import ParameterSweepPanel from './backtestResults/ParameterSweepPanel';
import ComparePanel from './backtestResults/ComparePanel';
import BasketPanel from './backtestResults/BasketPanel';
import { StatCard } from './backtestResults/utils';
import Button from '../components/Button';

// ── Nav-rail icons (inline SVG so we don't pull in an icon lib) ──
const iconProps = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IconOverview = () => (<svg {...iconProps}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>);
const IconTrades = () => (<svg {...iconProps}><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>);
const IconAnalytics = () => (<svg {...iconProps}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>);
const IconCompare = () => (<svg {...iconProps}><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg>);
const IconOptimize = () => (<svg {...iconProps}><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>);

// Five destinations. `authOnly` items need the authenticated strategy fetch
// (Quick Edit / Walk-Forward / Compare all operate on the real strategy).
// `needsTrades` items are hidden for empty (0-trade) runs.
const NAV_ITEMS = [
  { key: 'overview', label: 'Overview', Icon: IconOverview },
  { key: 'trades', label: 'Trades', Icon: IconTrades, needsTrades: true },
  { key: 'analytics', label: 'Analytics', Icon: IconAnalytics, needsTrades: true },
  { key: 'compare', label: 'Compare', Icon: IconCompare, authOnly: true },
  { key: 'optimize', label: 'Optimize', Icon: IconOptimize, authOnly: true },
];

export default function BacktestResults({ backtest, onImproved, onRerunResult, onRequiresSignup, onPaperTradeStarted }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('overview');
  const [compareTab, setCompareTab] = useState('basket');
  const [crosshairDate, setCrosshairDate] = useState(null);
  const [strategyForEdit, setStrategyForEdit] = useState(null);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [strategyError, setStrategyError] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const strategyId = backtest?.strategy_id || backtest?.strategy?.id || backtest?.strategy;
  // Owner-less runs are the shared/social links (e.g. posted on X). Viewers don't
  // own the underlying strategy, so offer to save a copy to their own collection.
  const isSharedView = backtest && !backtest.user;

  const totalTrades = backtest?.results?.total_trades;
  // A completed run that produced 0 trades is the "blank result" case — it churns
  // like a failure, so surface the AI-fix CTA instead of the paper-trading CTA.
  const isEmptyResult = backtest?.status === 'completed' && (totalTrades || 0) === 0;

  // Final activation-funnel step: results actually rendered. Keyed on the backtest id so a
  // re-run (which produces a new id) re-fires rather than being swallowed by memoization.
  useEffect(() => {
    if (!backtest?.id) return;
    track(Events.BACKTEST_RESULTS_VIEWED, { backtest_id: backtest.id, status: backtest.status });
    // Split the funnel: a traded result is real activation; an empty one is not.
    if (backtest.status === 'completed') {
      track(isEmptyResult ? Events.BACKTEST_EMPTY : Events.BACKTEST_TRADED, {
        backtest_id: backtest.id,
        total_trades: totalTrades || 0,
      });
    }
  }, [backtest?.id, backtest?.status, isEmptyResult, totalTrades]);

  useEffect(() => {
    if (!strategyId || !isAuthenticated) return;
    let mounted = true;
    setStrategyLoading(true);
    setStrategyError('');
    getStrategy(strategyId)
      .then((res) => {
        if (!mounted) return;
        setStrategyForEdit(res.data || null);
      })
      .catch(() => {
        if (!mounted) return;
        setStrategyForEdit(null);
        setStrategyError('Unable to load strategy details for quick edit.');
      })
      .finally(() => {
        if (mounted) setStrategyLoading(false);
      });
    return () => { mounted = false; };
  }, [strategyId, isAuthenticated]);

  if (!backtest) return null;

  const handleOpenBuilder = () => {
    if (strategyForEdit && onImproved) {
      onImproved(strategyForEdit);
      return;
    }
    if (strategyId && strategyForEdit == null) {
      setStrategyError('Strategy is still loading. Please try again in a moment.');
      return;
    }
    navigate('/builder');
  };

  const handleOpenShare = () => {
    track(Events.BACKTEST_SHARE_OPENED, { backtest_id: backtest?.id });
    setShowShare(true);
  };

  const handleOpenDownload = () => {
    track(Events.BACKTEST_DATA_DOWNLOAD_OPENED, { backtest_id: backtest?.id });
    setShowDownload(true);
  };

  const handleOpenExport = () => {
    if (!isAuthenticated) {
      onRequiresSignup && onRequiresSignup('Sign up to export strategies to code.');
      return;
    }
    track(Events.STRATEGY_EXPORT_OPENED, { source: 'backtest', backtest_id: backtest?.id });
    setShowExport(true);
  };

  // Save the shared strategy into the viewer's own collection as an editable copy.
  const handleSaveStrategy = async () => {
    if (!isAuthenticated) {
      onRequiresSignup && onRequiresSignup('Sign up to save strategies to your collection.');
      return;
    }
    if (!strategyId) return;
    setSaving(true);
    setSaveMsg('');
    setStrategyError('');
    try {
      const { data } = await duplicateStrategy(strategyId, { name: backtest.strategy_name });
      track(Events.BACKTEST_STRATEGY_SAVED, { backtest_id: backtest?.id, strategy_id: data?.id });
      setSaveMsg('Saved to your strategies.');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || err?.response?.data?.requires_signup) {
        onRequiresSignup && onRequiresSignup('Sign up to save strategies.');
        return;
      }
      setStrategyError(err?.response?.data?.detail || 'Could not save the strategy. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // From the empty-result modal: open the in-place Quick Edit (Analysis Tools tab)
  // rather than the builder, so the user can tweak params and re-run — and actually
  // save, since Quick Edit operates on their real strategy.
  const handleEditInQuickEdit = () => {
    if (!isAuthenticated) {
      onRequiresSignup && onRequiresSignup('Sign up to edit strategies.');
      return;
    }
    setActiveView('optimize');
    setTimeout(() => {
      const el = document.getElementById('quick-edit-anchor');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const hasTrades = (totalTrades || 0) > 0;
  // Filter the rail to what's actually reachable for this run/viewer, then make
  // sure the active view is still valid (e.g. guest can't sit on 'optimize').
  const navItems = NAV_ITEMS.filter((item) => {
    if (item.authOnly && !isAuthenticated) return false;
    if (item.needsTrades && !hasTrades) return false;
    return true;
  });
  const activeExists = navItems.some((i) => i.key === activeView);
  const currentView = activeExists ? activeView : 'overview';

  const goTo = (key) => {
    setActiveView(key);
    // Reset scroll so a swapped view starts at the top, not wherever the last did.
    const main = document.querySelector('.results-main');
    if (main) main.scrollTop = 0;
  };

  return (
    <div className="results-layout" style={{ display: 'flex', gap: 0, height: '100%' }}>
      <Helmet>
        <title>Backtest Results — Algovex</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Desktop: persistent vertical nav rail. One click to any destination. */}
      <nav className="results-rail" aria-label="Results sections">
        {navItems.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`results-rail-item${currentView === key ? ' active' : ''}`}
            onClick={() => goTo(key)}
            aria-current={currentView === key ? 'page' : undefined}
          >
            <span className="results-rail-icon"><Icon /></span>
            <span className="results-rail-label">{label}</span>
          </button>
        ))}
      </nav>

      <div className="results-main" style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 24 }}>
        <div className="results-tabbar">
          <h2 style={{ marginTop: 0, color: 'var(--text)', marginBottom: 0, fontSize: 'var(--fs-xl)' }}>Backtest Results</h2>
          {/* Actions live apart from navigation — they open modals / leave the page. */}
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="ghost" size="sm" onClick={handleOpenShare}>Share</Button>
            <Button variant="ghost" size="sm" onClick={handleOpenExport}>Export code</Button>
            {backtest.status === 'completed' && (
              <Button variant="ghost" size="sm" onClick={handleOpenDownload}>Download data</Button>
            )}
            {isSharedView && (
              <Button variant="primary" size="sm" onClick={handleSaveStrategy} loading={saving}>
                Save to my strategies
              </Button>
            )}
            {isAuthenticated && (
              <Button variant="ghost" size="sm" onClick={handleOpenBuilder}>Edit Strategy</Button>
            )}
          </div>
        </div>

        {/* Mobile: the same destinations as a horizontal scrollable strip. */}
        <div className="results-nav-mobile" role="tablist" aria-label="Results sections">
          {navItems.map(({ key, label, Icon }) => (
            <button
              key={key}
              className={`results-nav-mobile-item${currentView === key ? ' active' : ''}`}
              onClick={() => goTo(key)}
              role="tab"
              aria-selected={currentView === key}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {!isAuthenticated && (
          <div
            style={{
              display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)',
              alignItems: 'center', justifyContent: 'space-between',
              background: 'var(--panel-2, rgba(255,255,255,0.04))',
              border: '1px solid var(--border, rgba(255,255,255,0.08))',
              borderRadius: 'var(--radius-md, 10px)',
              padding: '12px 16px', margin: '4px 0 18px',
            }}
          >
            <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, minWidth: 200 }}>
              Like this? Build and backtest your own strategy on Algovex — no code required.
            </div>
            <Button size="sm" onClick={() => navigate('/auth')}>Sign up free</Button>
          </div>
        )}

        {saveMsg && (
          <div style={{
            marginBottom: 14, display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
            flexWrap: 'wrap', color: 'var(--green)', fontSize: 13, fontWeight: 600,
          }}>
            <span>✓ {saveMsg}</span>
            <Button variant="ghost" size="sm" onClick={() => navigate('/strategies')}>View my strategies</Button>
          </div>
        )}

        {strategyError && (
          <div style={{ marginBottom: 14, color: 'var(--red)', fontSize: 12 }}>{strategyError}</div>
        )}

        {/* Persistent scoreboard — keeps the run's key numbers in view on every
            screen except Overview, which already leads with the full stat grid. */}
        {currentView !== 'overview' && <SummaryStrip backtest={backtest} />}

        {currentView === 'overview' && (
          <>
            <StatsGrid backtest={backtest} />
            {isEmptyResult ? (
              <EmptyTradesCTA backtest={backtest} onEditInQuickEdit={handleEditInQuickEdit} onRetested={onRerunResult} onRequiresSignup={onRequiresSignup} />
            ) : (
              <PaperTradingCTA backtest={backtest} onRequiresSignup={onRequiresSignup} onPaperTradeStarted={onPaperTradeStarted} />
            )}
            <PriceChartSection backtest={backtest} onCrosshairMove={setCrosshairDate} />
            <EquityCurveSection backtest={backtest} crosshairDate={crosshairDate} />

            <div style={{ marginTop: 20, fontSize: 13, color: 'var(--muted)' }}>
              <strong>{(backtest.symbols && backtest.symbols.length > 1) ? 'Symbols:' : 'Symbol:'}</strong>{' '}
              {(backtest.symbols && backtest.symbols.length > 1)
                ? backtest.symbols.map((u) => u.symbol).join(', ')
                : backtest.symbol} &nbsp;|&nbsp;
              <strong>Asset:</strong> {backtest.asset_type} &nbsp;|&nbsp;
              <strong>Period:</strong> {backtest.start_date} → {backtest.end_date}
            </div>
          </>
        )}

        {currentView === 'trades' && (
          <TradeLogSection backtest={backtest} defaultOpen />
        )}

        {currentView === 'analytics' && (
          <>
            <IndicatorsSection backtest={backtest} />
            <CorrelationSection backtest={backtest} />
            <AdvancedAnalyticsSection backtest={backtest} defaultOpen />
          </>
        )}

        {currentView === 'compare' && (
          <>
            {/* Two peer comparison modes — a segmented toggle so the symbol-basket
                view is a visible sibling of variants, not buried below the fold. */}
            <div className="compare-subtabs" role="tablist" aria-label="Comparison mode">
              <button
                className={`compare-subtab${compareTab === 'basket' ? ' active' : ''}`}
                onClick={() => setCompareTab('basket')}
                role="tab"
                aria-selected={compareTab === 'basket'}
              >
                Symbol Basket
              </button>
              <button
                className={`compare-subtab${compareTab === 'variants' ? ' active' : ''}`}
                onClick={() => setCompareTab('variants')}
                role="tab"
                aria-selected={compareTab === 'variants'}
              >
                Strategy Variants
              </button>
            </div>

            {compareTab === 'variants' ? (
              <>
                <SectionHeading>Compare strategy variants</SectionHeading>
                <ComparePanel backtest={backtest} strategyForEdit={strategyForEdit} />
              </>
            ) : (
              <>
                <SectionHeading>Compare across a basket of symbols</SectionHeading>
                <BasketPanel backtest={backtest} />
              </>
            )}
          </>
        )}

        {currentView === 'optimize' && (
          <>
            <div id="quick-edit-anchor">
              <QuickEditPanel
                backtest={backtest}
                strategyForEdit={strategyForEdit}
                strategyLoading={strategyLoading}
                onStrategyUpdated={setStrategyForEdit}
                onRerunResult={onRerunResult}
              />
            </div>
            {backtest.status === 'completed' && (
              <ParameterSweepPanel
                backtest={backtest}
                strategyForEdit={strategyForEdit}
                onRerunResult={onRerunResult}
              />
            )}
            {backtest.status === 'completed' && (
              <WalkForwardPanel
                backtest={backtest}
                strategyId={strategyId}
                strategyForEdit={strategyForEdit}
                onStrategyUpdated={setStrategyForEdit}
                onRerunResult={onRerunResult}
              />
            )}
          </>
        )}
      </div>

      {showShare && (
        <ShareBacktestModal backtest={backtest} onClose={() => setShowShare(false)} />
      )}

      {showDownload && (
        <DownloadDataModal backtestId={backtest.id} onClose={() => setShowDownload(false)} />
      )}

      {showExport && (
        <ExportStrategyModal
          backtestId={backtest.id}
          name={backtest.strategy_name}
          onClose={() => setShowExport(false)}
          onRequiresSignup={onRequiresSignup}
        />
      )}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h3 style={{ margin: '0 0 12px', color: 'var(--text)', fontSize: 'var(--fs-lg, 17px)', fontWeight: 700 }}>
      {children}
    </h3>
  );
}

// Thin always-visible scoreboard so context isn't lost when the body swaps to a
// non-Overview screen. Mirrors the four headline numbers from StatsGrid.
function SummaryStrip({ backtest }) {
  const r = backtest.results || {};
  return (
    <div
      className="stat-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-4)',
      }}
    >
      <StatCard label="Return" value={r.return_pct != null ? `${r.return_pct}%` : '—'} color={r.return_pct >= 0 ? 'var(--green)' : 'var(--red)'} />
      <StatCard label="Sharpe Ratio" value={r.sharpe_ratio ?? '—'} />
      <StatCard label="Max Drawdown" value={r.max_drawdown_pct != null ? `${r.max_drawdown_pct}%` : '—'} />
      <StatCard label="Total Trades" value={r.total_trades ?? '—'} />
    </div>
  );
}
